import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import {
  MAX_POINTS,
  MIN_AREA,
  circleCenter,
  circleHandles,
  circlePoints,
  clamp01,
  colorOf,
  hitFrame,
  isCircleFrame,
  isRectFrame,
  polygonArea,
  rectEdgeHandles,
  rectPoints,
  resizeRectCorner,
  resizeRectEdge,
  toNorm,
  translatePoints,
  isSelfIntersecting,
} from '../../utils/regionFrameGeometry';

/**
 * #223029 — lớp VẼ KHUNG phủ trên ảnh mẫu camera (app Admin). Toạ độ 0..1 theo khung ảnh.
 * Chế độ: "select" (chọn/kéo đỉnh), "rect" (kéo chữ nhật), "circle" (giữ ở tâm kéo ra bán kính — 27/10/2026),
 * "poly" (chạm từng đỉnh đa giác).
 * (19/10/2026, lệnh sếp) Hai chế độ sửa: "select" = KÉO CẠNH (chữ nhật kéo góc/cạnh vẫn giữ chữ nhật, đa giác kéo
 * đỉnh) và "move" = DỜI CẢ VÙNG (chạm khung nào kéo nguyên khung đó). Mỗi lần kéo báo `onEditStart` (để Hoàn tác)
 * rồi `onReplacePoints(khung, điểm mới)`. Nút "Toàn màn hình" phủ kín màn hình + khoá cuộn trang để kéo không trôi.
 */
export default function RegionFrameEditor({
  imageUrl,
  frames,
  selected,
  mode,
  readOnly,
  onModeChange,
  onSelect,
  onDrawn,
  onEditStart,
  onReplacePoints,
  onUndo,
  canUndo,
  onImageSize,
  onRequestSave,
  canSave,
}) {
  const boxRef = useRef(null);
  const [aspect, setAspect] = useState(16 / 9);
  const [dragStart, setDragStart] = useState(null);
  const [dragNow, setDragNow] = useState(null);
  const [poly, setPoly] = useState([]);
  const [polyHover, setPolyHover] = useState(null);
  // Đang kéo: {type: 'move'|'corner'|'edge'|'point', f, k, start, orig}
  const [drag, setDrag] = useState(null);
  const [polyError, setPolyError] = useState(null);
  const [full, setFull] = useState(false);

  // Toàn màn hình: khoá cuộn trang (kéo khung không làm trang trôi), Esc để thoát.
  useEffect(() => {
    if (!full) return undefined;
    const old = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && setFull(false);
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = old;
      window.removeEventListener('keydown', onKey);
    };
  }, [full]);

  const norm = useCallback((e) => {
    const r = boxRef.current.getBoundingClientRect();
    return toNorm(e.clientX, e.clientY, r);
  }, []);

  const drawing = !readOnly && (mode === 'rect' || mode === 'circle' || mode === 'poly');
  const moving = !readOnly && mode === 'move';

  const finishPoly = useCallback(() => {
    if (poly.length < 3) return setPolyError('Đa giác cần ít nhất 3 điểm.');
    if (isSelfIntersecting(poly)) {
      return setPolyError('Các cạnh đang cắt chéo nhau — bấm “Làm lại” rồi chạm lại các đỉnh.');
    }
    if (polygonArea(poly) < MIN_AREA) return setPolyError('Khung quá nhỏ.');
    onDrawn('da_giac', poly);
    setPoly([]);
    setPolyHover(null);
    setPolyError(null);
  }, [poly, onDrawn]);

  const onPointerDown = (e) => {
    if (readOnly) return;
    const p = norm(e);
    if (mode === 'rect' || mode === 'circle') {
      e.currentTarget.setPointerCapture?.(e.pointerId);
      setDragStart(p);
      setDragNow(p);
      return;
    }
    if (mode === 'poly') {
      setPolyError(null);
      if (poly.length >= 3) {
        const [fx, fy] = poly[0];
        if (Math.hypot(p[0] - fx, p[1] - fy) < 0.03) return finishPoly();
      }
      if (poly.length >= MAX_POINTS) {
        return setPolyError('Tối đa 12 điểm — bấm “Xong” để đóng khung.');
      }
      setPoly([...poly, p]);
      return;
    }
    const hit = hitFrame(p[0], p[1], frames);
    onSelect(hit);
    // "Dời cả vùng": chạm vào khung nào là kéo nguyên khung đó luôn (không cần chọn trước).
    if (mode === 'move' && hit >= 0) beginDrag(e, 'move', hit);
  };

  const beginDrag = (e, type, f, k) => {
    if (readOnly || (mode !== 'select' && mode !== 'move')) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    onEditStart?.();
    setDrag({ type, f, k, start: norm(e), orig: frames[f].diem.map((q) => [q[0], q[1]]) });
  };

  const onPointerMove = (e) => {
    if ((mode === 'rect' || mode === 'circle') && dragStart) setDragNow(norm(e));
    else if (mode === 'poly') setPolyHover(norm(e));
    else if (drag) {
      const to = norm(e);
      const { type, f, k, start, orig } = drag;
      let next;
      if (type === 'move') next = translatePoints(orig, to[0] - start[0], to[1] - start[1]);
      else if (type === 'corner') next = resizeRectCorner(orig, k, to);
      else if (type === 'edge') next = resizeRectEdge(orig, k, to);
      else if (type === 'radius') next = circlePoints(circleCenter(orig), to, aspect);
      else next = orig.map((q, i) => (i === k ? [clamp01(to[0]), clamp01(to[1])] : q));
      onReplacePoints(f, next);
    }
  };

  const onPointerUp = (e) => {
    if (drag) return setDrag(null);
    if (mode === 'rect' && dragStart) {
      const pts = rectPoints(dragStart, norm(e));
      setDragStart(null);
      setDragNow(null);
      if (polygonArea(pts) >= MIN_AREA) onDrawn('chu_nhat', pts);
    }
    if (mode === 'circle' && dragStart) {
      const pts = circlePoints(dragStart, norm(e), aspect);
      setDragStart(null);
      setDragNow(null);
      if (polygonArea(pts) >= MIN_AREA) onDrawn('tron', pts);
    }
  };

  const preview = useMemo(
    () => {
      if (!dragStart || !dragNow) return null;
      return mode === 'circle' ? circlePoints(dragStart, dragNow, aspect) : rectPoints(dragStart, dragNow);
    },
    [dragStart, dragNow, mode, aspect],
  );

  const path = (pts, close = true) =>
    pts.map((p, i) => `${i ? 'L' : 'M'}${p[0]} ${p[1]}`).join(' ') + (close ? ' Z' : '');

  const tool = (key, label, active, onClick, disabled) => (
    <Button
      key={key}
      size="sm"
      variant={active ? 'primary' : 'outline-secondary'}
      disabled={disabled}
      onClick={onClick}
      data-testid={`cong-cu-${key}`}
    >
      {label}
    </Button>
  );

  const content = (
    <>
      <div
        ref={boxRef}
        data-testid="khung-anh"
        className="position-relative w-100 border rounded bg-dark overflow-hidden"
        style={{
          aspectRatio: String(aspect),
          userSelect: 'none',
          // Toàn màn hình: ảnh to nhất mà vẫn chừa chỗ cho thanh công cụ.
          ...(full ? { width: `min(100%, calc((100dvh - 150px) * ${aspect}))`, margin: '0 auto' } : {}),
        }}
      >
        <img
          src={imageUrl}
          alt="Ảnh mẫu camera"
          draggable={false}
          className="position-absolute top-0 start-0 w-100 h-100"
          style={{ objectFit: 'fill' }}
          onLoad={(e) => {
            const im = e.currentTarget;
            if (im.naturalWidth && im.naturalHeight) {
              setAspect(im.naturalWidth / im.naturalHeight);
              onImageSize?.(im.naturalWidth, im.naturalHeight);
            }
          }}
        />
        <svg
          className="position-absolute top-0 start-0 w-100 h-100"
          viewBox="0 0 1 1"
          preserveAspectRatio="none"
          style={{
            touchAction: full || drawing || moving ? 'none' : 'pan-y',
            cursor: drawing ? 'crosshair' : moving ? 'move' : 'default',
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={() => {
            setDragStart(null);
            setDragNow(null);
            setDrag(null);
          }}
        >
          {frames.map((f, i) => {
            const c = colorOf(f, i);
            const sel = i === selected;
            const excl = f.loai === 'khong_phuc_vu';
            return (
              <g key={f.key} data-testid={`khung-${i}`}>
                <path
                  d={path(f.diem)}
                  fill={c}
                  fillOpacity={excl ? 0.28 : sel ? 0.28 : 0.18}
                  stroke={c}
                  strokeDasharray={excl ? '0.012 0.008' : undefined}
                  vectorEffect="non-scaling-stroke"
                  data-testid={sel ? `than-khung-${i}` : undefined}
                  style={{
                    strokeWidth: sel ? 3 : 2,
                    // Khung đang chọn: giữ để kéo (không cuộn trang); khung khác vẫn vuốt cuộn trang được.
                    touchAction: sel && !readOnly && mode === 'select' ? 'none' : undefined,
                    cursor: sel && !readOnly && mode === 'select' ? 'move' : undefined,
                  }}
                  onPointerDown={sel && mode === 'select' ? (e) => beginDrag(e, 'move', i) : undefined}
                />
                {sel &&
                  !readOnly &&
                  mode === 'select' &&
                  (isRectFrame(f)
                    ? [
                        ...f.diem.map((p, k) => ({ p, type: 'corner', k })),
                        ...rectEdgeHandles(f.diem).map((p, k) => ({ p, type: 'edge', k })),
                      ]
                    : isCircleFrame(f)
                      ? circleHandles(f.diem).map((p, k) => ({ p, type: 'radius', k }))
                      : f.diem.map((p, k) => ({ p, type: 'point', k }))
                  ).map(({ p, type, k }) => (
                    <g key={`${type}${k}`} data-testid={`cham-${type}-${k}`} onPointerDown={(e) => beginDrag(e, type, i, k)} style={{ touchAction: 'none', cursor: 'grab' }}>
                      {/* vùng bấm to cho ngón tay, chấm nhìn thấy nhỏ */}
                      <circle cx={p[0]} cy={p[1]} r={0.05} fill="transparent" />
                      <circle
                        cx={p[0]}
                        cy={p[1]}
                        r={type === 'edge' ? 0.013 : 0.018}
                        fill={type === 'edge' ? c : '#fff'}
                        stroke={type === 'edge' ? '#fff' : c}
                        vectorEffect="non-scaling-stroke"
                        style={{ strokeWidth: 3 }}
                      />
                    </g>
                  ))}
              </g>
            );
          })}
          {preview && (
            <path
              d={path(preview)}
              fill="#fff"
              fillOpacity={0.15}
              stroke="#fff"
              strokeDasharray="0.01 0.01"
              vectorEffect="non-scaling-stroke"
              style={{ strokeWidth: 2 }}
            />
          )}
          {poly.length > 0 && (
            <g>
              <path
                d={path(polyHover ? [...poly, polyHover] : poly, false)}
                fill="none"
                stroke="#fff"
                vectorEffect="non-scaling-stroke"
                style={{ strokeWidth: 2 }}
              />
              {poly.map((p, i) => (
                <circle
                  key={i}
                  cx={p[0]}
                  cy={p[1]}
                  r={i === 0 ? 0.022 : 0.014}
                  fill={i === 0 ? '#facc15' : '#fff'}
                  stroke="#000"
                  vectorEffect="non-scaling-stroke"
                  style={{ strokeWidth: 1 }}
                />
              ))}
            </g>
          )}
        </svg>
      </div>

      {!readOnly && (
        <div className="d-flex flex-wrap gap-2 mt-2" role="toolbar" aria-label="Công cụ khoanh vùng">
          {tool('doi-ca-vung', 'Dời cả vùng', mode === 'move', () => { onModeChange('move'); setPoly([]); })}
          {tool('chon', 'Kéo cạnh', mode === 'select', () => { onModeChange('select'); setPoly([]); })}
          {tool('chu-nhat', 'Vẽ chữ nhật', mode === 'rect', () => { onModeChange('rect'); setPoly([]); })}
          {tool('hinh-tron', 'Vẽ hình tròn', mode === 'circle', () => { onModeChange('circle'); setPoly([]); })}
          {tool('da-giac', 'Vẽ đa giác', mode === 'poly', () => onModeChange('poly'))}
          {tool('hoan-tac', 'Hoàn tác', false, onUndo, !canUndo)}
          {full ? (
            <>
              {onRequestSave && (
                <Button size="sm" variant="success" data-testid="luu-toan-man-hinh" disabled={!canSave} onClick={onRequestSave}>
                  Lưu
                </Button>
              )}
              {tool('thu-nho', 'Thu nhỏ', false, () => setFull(false))}
            </>
          ) : (
            tool('toan-man-hinh', '⛶ Toàn màn hình', false, () => setFull(true))
          )}
        </div>
      )}

      {mode === 'poly' && !readOnly && (
        <div className="border rounded p-2 mt-2 small bg-light">
          <div>
            Bấm từng đỉnh trên ảnh ({poly.length}/{MAX_POINTS}). Bấm lại <b>chấm vàng</b> hoặc “Xong” để đóng khung.
          </div>
          {polyError && <div className="text-danger mt-1">{polyError}</div>}
          <div className="d-flex gap-2 mt-2">
            <Button size="sm" variant="outline-secondary" onClick={() => { setPoly([]); setPolyError(null); }}>
              Làm lại
            </Button>
            <Button size="sm" data-testid="da-giac-xong" disabled={poly.length < 3} onClick={finishPoly}>
              Xong
            </Button>
          </div>
        </div>
      )}
      {mode === 'rect' && !readOnly && (
        <p className="small text-muted mt-2 mb-0">Giữ và kéo trên ảnh để vẽ khung chữ nhật.</p>
      )}
      {mode === 'select' && !readOnly && frames.length > 0 && (
        <p className={`small mt-2 mb-0 ${full ? 'text-white-50' : 'text-muted'}`}>
          Kéo cạnh: bấm vào khung để chọn, kéo chấm trắng ở góc để đổi cỡ, chấm màu giữa cạnh để kéo riêng cạnh đó.
          Xong bấm <b>Lưu</b>.
        </p>
      )}
      {mode === 'move' && !readOnly && frames.length > 0 && (
        <p className={`small mt-2 mb-0 ${full ? 'text-white-50' : 'text-muted'}`}>
          Dời cả vùng: chạm vào khung nào rồi kéo — cả khung di chuyển theo. Xong bấm <b>Lưu</b>.
        </p>
      )}
    </>
  );

  if (!full) return <div>{content}</div>;
  return (
    <div
      data-testid="toan-man-hinh"
      style={{
        position: 'fixed',
        inset: 0,
        // Dưới hộp thoại (1050+) để hộp đặt tên khung / xác nhận lưu vẫn hiện trên cùng.
        zIndex: 1040,
        background: '#111',
        padding: 8,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        overflow: 'hidden',
        overscrollBehavior: 'contain',
      }}
    >
      {content}
    </div>
  );
}
